import { useState, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { Users, PieChart, TrendingUp, ArrowLeft, Download, Filter, X, CalendarIcon } from "lucide-react";
import { PieChart as RechartsPieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from "recharts";
import { format } from "date-fns";
import { it, enUS, es } from "date-fns/locale";
import AdminLayout from "@/components/admin/AdminLayout";
import { BrutalCard } from "@/components/ui/BrutalCard";
import { BrutalButton } from "@/components/ui/BrutalButton";
import { 
  BrutalSelect, 
  BrutalSelectTrigger, 
  BrutalSelectValue, 
  BrutalSelectContent, 
  BrutalSelectItem 
} from "@/components/ui/BrutalSelect";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb";

interface DemographicsStats {
  totalUsers: number;
  respondedUsers: number;
  responseRate: number;
  filteredCount: number;
  ageRanges: Record<string, number>;
  genderIdentity: Record<string, number>;
  sexualOrientation: Record<string, number>;
  neurodivergences: Record<string, number>;
  ethnicity: Record<string, number>;
  workStatus: Record<string, number>;
  disabilities: Record<string, number>;
  rawRecords: RawRecord[];
}

interface RawRecord {
  date_of_birth: string | null;
  age_range: string | null;
  gender_identity: string | null;
  sexual_orientation: string | null;
  work_status: string | null;
  ethnicity: string | null;
  neurodivergences: string | null;
  disabilities: string | null;
}

interface Filters {
  birthDateFrom?: Date;
  birthDateTo?: Date;
  genderIdentity?: string;
  sexualOrientation?: string;
  workStatus?: string;
  ethnicity?: string;
}

const CHART_COLORS = [
  "hsl(var(--primary))",
  "hsl(var(--secondary))",
  "hsl(var(--accent))",
  "#8884d8",
  "#82ca9d",
  "#ffc658",
  "#ff7300",
  "#00C49F",
  "#FFBB28",
  "#FF8042",
];

// Mapping from database keys (kebab-case) to translation keys (camelCase)
const KEY_MAPPING: Record<string, string> = {
  // Gender
  "non-binary": "nonBinary",
  // Work Status
  "job-seeker": "jobSeeker",
  // Ethnicity
  "caucasian-european": "caucasian",
  "latin-american": "latinAmerican",
  "middle-eastern": "middleEastern",
  // Disabilities
  "invisible-chronic": "invisibleChronic",
};

const UserInsights = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();

  const getDateLocale = () => {
    switch (i18n.language?.substring(0, 2)) {
      case "it": return it;
      case "es": return es;
      default: return enUS;
    }
  };

  const currentYear = new Date().getFullYear();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState<DemographicsStats | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [filters, setFilters] = useState<Filters>({});
  const [exporting, setExporting] = useState(false);
  const [filtersOpen, setFiltersOpen] = useState(false);

  // Custom neobrutalist tooltip - inside component for t() access
  const BrutalTooltip = ({ active, payload, label }: any) => {
    if (!active || !payload?.length) return null;
    return (
      <div className="bg-card border-[3px] border-dark p-3 shadow-[4px_4px_0px_0px] shadow-dark font-body">
        <p className="font-bold">{label || payload[0].name}</p>
        <p>{payload[0].value} {t("admin.userInsights.users")}</p>
      </div>
    );
  };

  const loadStats = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const session = await supabase.auth.getSession();
      const token = session.data.session?.access_token;

      const { data, error } = await supabase.functions.invoke("get-demographics-stats", {
        headers: {
          Authorization: `Bearer ${token}`,
        },
        body: {
          birthDateFrom: filters.birthDateFrom ? format(filters.birthDateFrom, "yyyy-MM-dd") : undefined,
          birthDateTo: filters.birthDateTo ? format(filters.birthDateTo, "yyyy-MM-dd") : undefined,
          genderIdentity: filters.genderIdentity || undefined,
          sexualOrientation: filters.sexualOrientation || undefined,
          workStatus: filters.workStatus || undefined,
          ethnicity: filters.ethnicity || undefined,
        },
      });

      if (error) throw error;
      if (data?.error) throw new Error(data.error);

      setStats(data);
    } catch (err: any) {
      console.error("Error loading demographics stats:", err);
      setError(err.message || t("common.error"));
    } finally {
      setLoading(false);
    }
  }, [filters, t]);

  useEffect(() => {
    loadStats();
  }, [loadStats]);

  // Export CSV function
  const exportCSV = () => {
    if (!stats?.rawRecords || stats.rawRecords.length === 0) return;
    
    setExporting(true);
    
    const headers = [
      t("admin.userInsights.csvHeaders.dateOfBirth"),
      t("admin.userInsights.csvHeaders.ageRange"),
      t("admin.userInsights.csvHeaders.gender"),
      t("admin.userInsights.csvHeaders.orientation"),
      t("admin.userInsights.csvHeaders.workStatus"),
      t("admin.userInsights.csvHeaders.ethnicity"),
      t("admin.userInsights.csvHeaders.neurodivergences"),
      t("admin.userInsights.csvHeaders.disabilities"),
    ];
    
    const escapeCSV = (value: string | null): string => {
      if (!value) return "";
      // Escape quotes and wrap in quotes if contains comma, quote, or newline
      if (value.includes(",") || value.includes('"') || value.includes("\n")) {
        return `"${value.replace(/"/g, '""')}"`;
      }
      return value;
    };
    
    const csvContent = [
      headers.join(","),
      ...stats.rawRecords.map(r => [
        escapeCSV(r.date_of_birth),
        escapeCSV(r.age_range),
        escapeCSV(r.gender_identity),
        escapeCSV(r.sexual_orientation),
        escapeCSV(r.work_status),
        escapeCSV(r.ethnicity),
        escapeCSV(r.neurodivergences),
        escapeCSV(r.disabilities),
      ].join(","))
    ].join("\n");
    
    const blob = new Blob(["\uFEFF" + csvContent], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `${t("admin.userInsights.export.filename")}-${format(new Date(), "yyyy-MM-dd")}.csv`;
    link.click();
    URL.revokeObjectURL(url);
    
    setExporting(false);
  };

  // Count active filters
  const activeFiltersCount = Object.values(filters).filter(Boolean).length;

  // Format chart data with proper translation key mapping
  const formatChartData = (data: Record<string, number>, category: string) => {
    return Object.entries(data)
      .filter(([_, value]) => value > 0)
      .map(([key, value]) => {
        // Convert database key (kebab-case) to translation key (camelCase)
        const translationKey = KEY_MAPPING[key] || key;
        // For ageRanges, use userInsights namespace; for others, use onboarding.options
        const translatedName = category === "ageRanges"
          ? t(`admin.userInsights.ageRanges.${key}`, { defaultValue: key })
          : t(`onboarding.options.${category}.${translationKey}`, { defaultValue: key });
        return {
          name: translatedName,
          value,
        };
      });
  };

  const StatCard = ({ icon: Icon, title, value, subtitle }: { icon: any; title: string; value: string | number; subtitle?: string }) => (
    <BrutalCard className="p-6">
      <div className="flex items-start gap-4">
        <div className="p-3 bg-primary/10 rounded-lg">
          <Icon className="h-6 w-6 text-primary" />
        </div>
        <div>
          <p className="text-sm text-muted-foreground font-medium">{title}</p>
          <p className="text-2xl font-heading font-bold">{value}</p>
          {subtitle && <p className="text-xs text-muted-foreground">{subtitle}</p>}
        </div>
      </div>
    </BrutalCard>
  );

  const ChartCard = ({ title, children }: { title: string; children: React.ReactNode }) => (
    <BrutalCard className="p-6">
      <h3 className="font-heading font-bold text-xl mb-4 uppercase">{title}</h3>
      {children}
    </BrutalCard>
  );

  // Filter options - aligned with DemographicsFields.tsx database values
  const genderOptions = [
    { value: "", label: t("admin.userInsights.filters.allValues") },
    { value: "woman", label: t("onboarding.options.gender.woman") },
    { value: "man", label: t("onboarding.options.gender.man") },
    { value: "non-binary", label: t("onboarding.options.gender.nonBinary") },
    { value: "other", label: t("onboarding.options.gender.other") },
    { value: "prefer-not-to-say", label: t("onboarding.options.preferNotToSay") },
  ];

  const orientationOptions = [
    { value: "", label: t("admin.userInsights.filters.allValues") },
    { value: "heterosexual", label: t("onboarding.options.orientation.heterosexual") },
    { value: "gay", label: t("onboarding.options.orientation.gay") },
    { value: "lesbian", label: t("onboarding.options.orientation.lesbian") },
    { value: "bisexual", label: t("onboarding.options.orientation.bisexual") },
    { value: "pansexual", label: t("onboarding.options.orientation.pansexual") },
    { value: "asexual", label: t("onboarding.options.orientation.asexual") },
    { value: "queer", label: t("onboarding.options.orientation.queer") },
    { value: "other", label: t("onboarding.options.orientation.other") },
    { value: "prefer-not-to-say", label: t("onboarding.options.preferNotToSay") },
  ];

  const workStatusOptions = [
    { value: "", label: t("admin.userInsights.filters.allValues") },
    { value: "employee", label: t("onboarding.options.workStatus.employee") },
    { value: "freelance", label: t("onboarding.options.workStatus.freelance") },
    { value: "entrepreneur", label: t("onboarding.options.workStatus.entrepreneur") },
    { value: "student", label: t("onboarding.options.workStatus.student") },
    { value: "job-seeker", label: t("onboarding.options.workStatus.jobSeeker") },
    { value: "other", label: t("onboarding.options.workStatus.other") },
    { value: "prefer-not-to-say", label: t("onboarding.options.preferNotToSay") },
  ];

  const ethnicityOptions = [
    { value: "", label: t("admin.userInsights.filters.allValues") },
    { value: "caucasian-european", label: t("onboarding.options.ethnicity.caucasian") },
    { value: "african", label: t("onboarding.options.ethnicity.african") },
    { value: "asian", label: t("onboarding.options.ethnicity.asian") },
    { value: "latin-american", label: t("onboarding.options.ethnicity.latinAmerican") },
    { value: "middle-eastern", label: t("onboarding.options.ethnicity.middleEastern") },
    { value: "mixed", label: t("onboarding.options.ethnicity.mixed") },
    { value: "other", label: t("onboarding.options.ethnicity.other") },
    { value: "prefer-not-to-say", label: t("onboarding.options.preferNotToSay") },
  ];

  if (loading && !stats) {
    return (
      <AdminLayout>
        <div className="p-8 space-y-6">
          <div className="animate-pulse space-y-4">
            <div className="h-8 bg-muted rounded w-1/3"></div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {[1, 2, 3].map((i) => (
                <div key={i} className="h-24 bg-muted rounded"></div>
              ))}
            </div>
          </div>
        </div>
      </AdminLayout>
    );
  }

  if (error) {
    return (
      <AdminLayout>
        <div className="p-8">
          <BrutalCard className="p-6 text-center">
            <p className="text-destructive">{error}</p>
          </BrutalCard>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="p-8 space-y-6">
        {/* Breadcrumb */}
        <Breadcrumb className="mb-6">
          <BreadcrumbList>
            <BreadcrumbItem>
              <BreadcrumbLink href="/admin/dashboard">Dashboard</BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator />
            <BreadcrumbItem>
              <BreadcrumbPage>{t("admin.userInsights.title")}</BreadcrumbPage>
            </BreadcrumbItem>
          </BreadcrumbList>
        </Breadcrumb>

        {/* Header */}
        <div className="flex items-center gap-4 mb-4">
          <button
            onClick={() => navigate("/admin/dashboard")}
            className="p-2 hover:bg-accent transition-colors rounded-lg"
            aria-label={t("common.back")}
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div>
            <h1 className="font-heading font-bold text-4xl flex items-center gap-3">
              <Users className="w-10 h-10 text-primary" />
              {t("admin.userInsights.title")}
            </h1>
            <p className="text-muted-foreground mt-2">
              {t("admin.userInsights.description")}
            </p>
          </div>
        </div>

        {/* Filters Toggle + Export Row */}
        <div className="flex items-center justify-between gap-4">
          <BrutalButton 
            variant={filtersOpen || activeFiltersCount > 0 ? "primary" : "outline"}
            onClick={() => setFiltersOpen(!filtersOpen)}
          >
            <Filter className="w-4 h-4 mr-2" />
            {filtersOpen 
              ? t("admin.userInsights.filters.hideFilters")
              : t("admin.userInsights.filters.showFilters")
            }
            {activeFiltersCount > 0 && (
              <span className="ml-2 bg-dark text-card px-2 py-0.5 text-xs rounded-full">
                {activeFiltersCount}
              </span>
            )}
          </BrutalButton>

          {/* Export CSV */}
          <BrutalButton 
            onClick={exportCSV}
            disabled={exporting || !stats?.rawRecords?.length}
          >
            <Download className="w-4 h-4 mr-2" />
            {exporting 
              ? t("admin.userInsights.export.exporting") 
              : t("admin.userInsights.export.button")
            }
          </BrutalButton>
        </div>

        {/* Collapsible Filters Panel */}
        {filtersOpen && (
          <BrutalCard className="p-4">
            <div className="flex flex-wrap items-center gap-3">
              {/* Date From */}
              <Popover>
                <PopoverTrigger asChild>
                  <BrutalButton variant="outline" size="sm" className="min-w-[140px] justify-start">
                    <CalendarIcon className="w-4 h-4 mr-2" />
                    {filters.birthDateFrom 
                      ? format(filters.birthDateFrom, "dd/MM/yyyy")
                      : t("admin.userInsights.filters.birthDateFrom")
                    }
                  </BrutalButton>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="single"
                    selected={filters.birthDateFrom}
                    onSelect={(date) => setFilters(f => ({ ...f, birthDateFrom: date || undefined }))}
                    disabled={(date) => date > new Date() || date < new Date("1920-01-01")}
                    captionLayout="dropdown"
                    fromYear={1920}
                    toYear={currentYear}
                    locale={getDateLocale()}
                    className="p-3 pointer-events-auto"
                    initialFocus
                  />
                </PopoverContent>
              </Popover>

              {/* Date To */}
              <Popover>
                <PopoverTrigger asChild>
                  <BrutalButton variant="outline" size="sm" className="min-w-[140px] justify-start">
                    <CalendarIcon className="w-4 h-4 mr-2" />
                    {filters.birthDateTo 
                      ? format(filters.birthDateTo, "dd/MM/yyyy")
                      : t("admin.userInsights.filters.birthDateTo")
                    }
                  </BrutalButton>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="single"
                    selected={filters.birthDateTo}
                    onSelect={(date) => setFilters(f => ({ ...f, birthDateTo: date || undefined }))}
                    disabled={(date) => date > new Date() || date < new Date("1920-01-01")}
                    captionLayout="dropdown"
                    fromYear={1920}
                    toYear={currentYear}
                    locale={getDateLocale()}
                    className="p-3 pointer-events-auto"
                    initialFocus
                  />
                </PopoverContent>
              </Popover>

              {/* Gender Select */}
              <BrutalSelect
                value={filters.genderIdentity || ""}
                onValueChange={(v) => setFilters(f => ({ ...f, genderIdentity: v === "_all" ? undefined : v || undefined }))}
              >
                <BrutalSelectTrigger className="min-w-[140px]">
                  <BrutalSelectValue placeholder={t("admin.userInsights.filters.gender")} />
                </BrutalSelectTrigger>
                <BrutalSelectContent>
                  {genderOptions.map((opt) => (
                    <BrutalSelectItem key={opt.value} value={opt.value || "_all"}>
                      {opt.label}
                    </BrutalSelectItem>
                  ))}
                </BrutalSelectContent>
              </BrutalSelect>

              {/* Orientation Select */}
              <BrutalSelect
                value={filters.sexualOrientation || ""}
                onValueChange={(v) => setFilters(f => ({ ...f, sexualOrientation: v === "_all" ? undefined : v || undefined }))}
              >
                <BrutalSelectTrigger className="min-w-[140px]">
                  <BrutalSelectValue placeholder={t("admin.userInsights.filters.orientation")} />
                </BrutalSelectTrigger>
                <BrutalSelectContent>
                  {orientationOptions.map((opt) => (
                    <BrutalSelectItem key={opt.value} value={opt.value || "_all"}>
                      {opt.label}
                    </BrutalSelectItem>
                  ))}
                </BrutalSelectContent>
              </BrutalSelect>

              {/* Work Status Select */}
              <BrutalSelect
                value={filters.workStatus || ""}
                onValueChange={(v) => setFilters(f => ({ ...f, workStatus: v === "_all" ? undefined : v || undefined }))}
              >
                <BrutalSelectTrigger className="min-w-[140px]">
                  <BrutalSelectValue placeholder={t("admin.userInsights.filters.workStatus")} />
                </BrutalSelectTrigger>
                <BrutalSelectContent>
                  {workStatusOptions.map((opt) => (
                    <BrutalSelectItem key={opt.value} value={opt.value || "_all"}>
                      {opt.label}
                    </BrutalSelectItem>
                  ))}
                </BrutalSelectContent>
              </BrutalSelect>

              {/* Ethnicity Select */}
              <BrutalSelect
                value={filters.ethnicity || ""}
                onValueChange={(v) => setFilters(f => ({ ...f, ethnicity: v === "_all" ? undefined : v || undefined }))}
              >
                <BrutalSelectTrigger className="min-w-[140px]">
                  <BrutalSelectValue placeholder={t("admin.userInsights.filters.ethnicity")} />
                </BrutalSelectTrigger>
                <BrutalSelectContent>
                  {ethnicityOptions.map((opt) => (
                    <BrutalSelectItem key={opt.value} value={opt.value || "_all"}>
                      {opt.label}
                    </BrutalSelectItem>
                  ))}
                </BrutalSelectContent>
              </BrutalSelect>

              {/* Clear Filters */}
              {activeFiltersCount > 0 && (
                <Button 
                  variant="ghost" 
                  size="sm"
                  onClick={() => setFilters({})}
                >
                  <X className="w-4 h-4 mr-1" />
                  {t("admin.userInsights.filters.clearFilters")}
                </Button>
              )}
            </div>

            {/* Active filters info */}
            {activeFiltersCount > 0 && (
              <p className="text-sm text-muted-foreground mt-3">
                {t("admin.userInsights.filters.activeFilters")}: {activeFiltersCount}
                {stats?.filteredCount !== undefined && ` • ${stats.filteredCount} ${t("admin.userInsights.filteredUsers")}`}
              </p>
            )}
          </BrutalCard>
        )}

        {/* KPI Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <StatCard
            icon={Users}
            title={t("admin.userInsights.totalUsers")}
            value={stats?.totalUsers || 0}
          />
          <StatCard
            icon={PieChart}
            title={t("admin.userInsights.respondedUsers")}
            value={stats?.respondedUsers || 0}
          />
          <StatCard
            icon={TrendingUp}
            title={t("admin.userInsights.responseRate")}
            value={`${(stats?.responseRate || 0).toFixed(1)}%`}
          />
        </div>

        {/* Charts */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Age Distribution */}
          {stats?.ageRanges && Object.keys(stats.ageRanges).length > 0 && (
            <ChartCard title={t("admin.userInsights.charts.ageDistribution")}>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={formatChartData(stats.ageRanges, "ageRanges")}>
                  <CartesianGrid strokeWidth={3} stroke="hsl(var(--dark))" />
                  <XAxis 
                    dataKey="name" 
                    fontSize={12}
                    stroke="hsl(var(--dark))"
                    style={{ fontFamily: 'var(--font-body)', fontWeight: 'bold' }}
                  />
                  <YAxis 
                    stroke="hsl(var(--dark))"
                    style={{ fontFamily: 'var(--font-body)', fontWeight: 'bold' }}
                  />
                  <Tooltip content={<BrutalTooltip />} />
                  <Bar 
                    dataKey="value" 
                    fill="hsl(var(--primary))"
                    stroke="hsl(var(--dark))"
                    strokeWidth={3}
                  />
                </BarChart>
              </ResponsiveContainer>
            </ChartCard>
          )}

          {/* Gender Distribution */}
          {stats?.genderIdentity && Object.keys(stats.genderIdentity).length > 0 && (
            <ChartCard title={t("admin.userInsights.charts.genderDistribution")}>
              <ResponsiveContainer width="100%" height={300}>
                <RechartsPieChart>
                  <Pie
                    data={formatChartData(stats.genderIdentity, "gender")}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    outerRadius={100}
                    fill="#8884d8"
                    dataKey="value"
                    stroke="hsl(var(--dark))"
                    strokeWidth={3}
                    label={({ name, percent }) => `${name} (${(percent * 100).toFixed(0)}%)`}
                  >
                    {formatChartData(stats.genderIdentity, "gender").map((_, index) => (
                      <Cell 
                        key={`cell-${index}`} 
                        fill={CHART_COLORS[index % CHART_COLORS.length]}
                        stroke="hsl(var(--dark))"
                        strokeWidth={3}
                      />
                    ))}
                  </Pie>
                  <Tooltip content={<BrutalTooltip />} />
                </RechartsPieChart>
              </ResponsiveContainer>
            </ChartCard>
          )}

          {/* Sexual Orientation */}
          {stats?.sexualOrientation && Object.keys(stats.sexualOrientation).length > 0 && (
            <ChartCard title={t("admin.userInsights.charts.orientationDistribution")}>
              <ResponsiveContainer width="100%" height={300}>
                <RechartsPieChart>
                  <Pie
                    data={formatChartData(stats.sexualOrientation, "orientation")}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    outerRadius={100}
                    fill="#8884d8"
                    dataKey="value"
                    stroke="hsl(var(--dark))"
                    strokeWidth={3}
                    label={({ name, percent }) => `${name} (${(percent * 100).toFixed(0)}%)`}
                  >
                    {formatChartData(stats.sexualOrientation, "orientation").map((_, index) => (
                      <Cell 
                        key={`cell-${index}`} 
                        fill={CHART_COLORS[index % CHART_COLORS.length]}
                        stroke="hsl(var(--dark))"
                        strokeWidth={3}
                      />
                    ))}
                  </Pie>
                  <Tooltip content={<BrutalTooltip />} />
                </RechartsPieChart>
              </ResponsiveContainer>
            </ChartCard>
          )}

          {/* Work Status */}
          {stats?.workStatus && Object.keys(stats.workStatus).length > 0 && (
            <ChartCard title={t("admin.userInsights.charts.workStatusDistribution")}>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={formatChartData(stats.workStatus, "workStatus")} layout="vertical">
                  <CartesianGrid strokeWidth={3} stroke="hsl(var(--dark))" />
                  <XAxis 
                    type="number"
                    stroke="hsl(var(--dark))"
                    style={{ fontFamily: 'var(--font-body)', fontWeight: 'bold' }}
                  />
                  <YAxis 
                    dataKey="name" 
                    type="category" 
                    width={120} 
                    fontSize={12}
                    stroke="hsl(var(--dark))"
                    style={{ fontFamily: 'var(--font-body)', fontWeight: 'bold' }}
                  />
                  <Tooltip content={<BrutalTooltip />} />
                  <Bar 
                    dataKey="value" 
                    fill="hsl(var(--secondary))"
                    stroke="hsl(var(--dark))"
                    strokeWidth={3}
                  />
                </BarChart>
              </ResponsiveContainer>
            </ChartCard>
          )}

          {/* Neurodivergences */}
          {stats?.neurodivergences && Object.keys(stats.neurodivergences).length > 0 && (
            <ChartCard title={t("admin.userInsights.charts.neurodivergencesDistribution")}>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={formatChartData(stats.neurodivergences, "neurodivergences")} layout="vertical">
                  <CartesianGrid strokeWidth={3} stroke="hsl(var(--dark))" />
                  <XAxis 
                    type="number"
                    stroke="hsl(var(--dark))"
                    style={{ fontFamily: 'var(--font-body)', fontWeight: 'bold' }}
                  />
                  <YAxis 
                    dataKey="name" 
                    type="category" 
                    width={120} 
                    fontSize={12}
                    stroke="hsl(var(--dark))"
                    style={{ fontFamily: 'var(--font-body)', fontWeight: 'bold' }}
                  />
                  <Tooltip content={<BrutalTooltip />} />
                  <Bar 
                    dataKey="value" 
                    fill="hsl(var(--accent))"
                    stroke="hsl(var(--dark))"
                    strokeWidth={3}
                  />
                </BarChart>
              </ResponsiveContainer>
            </ChartCard>
          )}

          {/* Disabilities */}
          {stats?.disabilities && Object.keys(stats.disabilities).length > 0 && (
            <ChartCard title={t("admin.userInsights.charts.disabilitiesDistribution")}>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={formatChartData(stats.disabilities, "disabilities")} layout="vertical">
                  <CartesianGrid strokeWidth={3} stroke="hsl(var(--dark))" />
                  <XAxis 
                    type="number"
                    stroke="hsl(var(--dark))"
                    style={{ fontFamily: 'var(--font-body)', fontWeight: 'bold' }}
                  />
                  <YAxis 
                    dataKey="name" 
                    type="category" 
                    width={120} 
                    fontSize={12}
                    stroke="hsl(var(--dark))"
                    style={{ fontFamily: 'var(--font-body)', fontWeight: 'bold' }}
                  />
                  <Tooltip content={<BrutalTooltip />} />
                  <Bar 
                    dataKey="value" 
                    fill="#8884d8"
                    stroke="hsl(var(--dark))"
                    strokeWidth={3}
                  />
                </BarChart>
              </ResponsiveContainer>
            </ChartCard>
          )}

          {/* Ethnicity */}
          {stats?.ethnicity && Object.keys(stats.ethnicity).length > 0 && (
            <ChartCard title={t("admin.userInsights.charts.ethnicityDistribution")}>
              <ResponsiveContainer width="100%" height={300}>
                <RechartsPieChart>
                  <Pie
                    data={formatChartData(stats.ethnicity, "ethnicity")}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    outerRadius={100}
                    fill="#8884d8"
                    dataKey="value"
                    stroke="hsl(var(--dark))"
                    strokeWidth={3}
                    label={({ name, percent }) => `${name} (${(percent * 100).toFixed(0)}%)`}
                  >
                    {formatChartData(stats.ethnicity, "ethnicity").map((_, index) => (
                      <Cell 
                        key={`cell-${index}`} 
                        fill={CHART_COLORS[index % CHART_COLORS.length]}
                        stroke="hsl(var(--dark))"
                        strokeWidth={3}
                      />
                    ))}
                  </Pie>
                  <Tooltip content={<BrutalTooltip />} />
                </RechartsPieChart>
              </ResponsiveContainer>
            </ChartCard>
          )}
        </div>

        {/* Privacy Note */}
        <BrutalCard className="p-4 bg-muted/50">
          <p className="text-sm text-muted-foreground text-center">
            {t("admin.userInsights.privacyNote")}
          </p>
        </BrutalCard>
      </div>
    </AdminLayout>
  );
};

export default UserInsights;
